import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Upload, Plus, Trash2, Database, FileSpreadsheet, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  getTransactions, addManualTransaction, uploadCSV, uploadPDF,
  loadSampleTransactions, updateTransaction, deleteTransaction,
  clearTransactions, uploadBulkPDFs,
} from "@/lib/api";

const CATEGORIES = [
  "Revenue / Deposits", "Payroll", "Rent", "Merchant Processing",
  "Loan Payments", "Utilities", "Transfers", "Owner Draws",
  "Other Operating Expenses",
];

const categoryColors = {
  "Revenue / Deposits": "bg-[#2D4A3E] text-white",
  "Payroll": "bg-[#E07A5F] text-white",
  "Rent": "bg-[#E3B505] text-[#1C1917]",
  "Loan Payments": "bg-[#8A9A86] text-white",
  "Utilities": "bg-[#57534E] text-white",
  "Transfers": "bg-[#78716C] text-white",
  "Owner Draws": "bg-[#1A362D] text-white",
  "Merchant Processing": "bg-[#264B40] text-white",
  "Other Operating Expenses": "bg-[#F1EFEA] text-[#1C1917]",
};

export default function BankStatements({ onDataChange, analysisId, refreshKey, requireAuth }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [form, setForm] = useState({ date: "", description: "", amount: "" });

  const fetchData = useCallback(async () => {
    try {
      const res = await getTransactions(analysisId);
      setTransactions(res.data.transactions || []);
    } catch (e) {
      console.error(e);
    }
  }, [analysisId]);

  useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

  const handleUploadFile = async (e) => {
    if (requireAuth && !requireAuth()) { e.target.value = ""; return; }
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setLoading(true);
    const pdfFiles = files.filter((f) => f.name.toLowerCase().endsWith(".pdf"));
    const csvFiles = files.filter((f) => f.name.toLowerCase().endsWith(".csv"));
    let totalCount = 0;
    try {
      if (pdfFiles.length > 1) {
        const res = await uploadBulkPDFs(pdfFiles, analysisId);
        if (res.data.error) { toast.error(res.data.error); }
        else { totalCount += res.data.total_count; toast.success(`Parsed ${res.data.total_count} transactions from ${pdfFiles.length} PDFs`); }
      } else if (pdfFiles.length === 1) {
        const res = await uploadPDF(pdfFiles[0], analysisId);
        if (res.data.error) { toast.error(res.data.error); }
        else { totalCount += res.data.count; toast.success(`Parsed ${res.data.count} transactions from PDF`); }
      }
      for (const csv of csvFiles) {
        const res = await uploadCSV(csv, analysisId);
        if (res.data.error) { toast.error(res.data.error); }
        else { totalCount += res.data.count; toast.success(`Parsed ${res.data.count} transactions from ${csv.name}`); }
      }
      if (totalCount > 0) { fetchData(); onDataChange?.(); }
    } catch (err) {
      toast.error("Failed to parse files");
    }
    setLoading(false);
    e.target.value = "";
  };

  const handleLoadSample = async () => {
    setLoading(true);
    try {
      await loadSampleTransactions(analysisId);
      toast.success("Sample bank statement data loaded");
      fetchData();
      onDataChange?.();
    } catch (err) {
      toast.error("Failed to load sample data");
    }
    setLoading(false);
  };

  const handleAddManual = async () => {
    if (requireAuth && !requireAuth()) return;
    if (!form.date || !form.description || !form.amount) {
      toast.error("Please fill all fields");
      return;
    }
    try {
      await addManualTransaction({
        date: form.date,
        description: form.description,
        amount: parseFloat(form.amount),
      }, analysisId);
      toast.success("Transaction added");
      setForm({ date: "", description: "", amount: "" });
      setShowManual(false);
      fetchData();
      onDataChange?.();
    } catch (err) {
      toast.error("Failed to add transaction");
    }
  };

  const handleCategoryChange = async (txId, newCategory) => {
    try {
      await updateTransaction(txId, { category: newCategory });
      setTransactions((prev) =>
        prev.map((t) => (t.id === txId ? { ...t, category: newCategory } : t))
      );
    } catch (err) {
      toast.error("Failed to update category");
    }
  };

  const handleDelete = async (txId) => {
    try {
      await deleteTransaction(txId);
      setTransactions((prev) => prev.filter((t) => t.id !== txId));
      onDataChange?.();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleClearAll = async () => {
    try {
      await clearTransactions(analysisId);
      setTransactions([]);
      toast.success("All transactions cleared");
      onDataChange?.();
    } catch (err) {
      toast.error("Failed to clear");
    }
  };

  const monthlySummary = transactions.reduce((acc, tx) => {
    const m = tx.month || "unknown";
    if (!acc[m]) acc[m] = { deposits: 0, withdrawals: 0, count: 0 };
    if (tx.amount > 0) acc[m].deposits += tx.amount;
    else acc[m].withdrawals += Math.abs(tx.amount);
    acc[m].count++;
    return acc;
  }, {});

  return (
    <div className="space-y-6 animate-fade-in" data-testid="bank-statements-section">
      {/* Action Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <label data-testid="upload-csv-btn" className="cursor-pointer" onClick={(e) => { if (requireAuth && !requireAuth()) e.preventDefault(); }}>
          <input type="file" accept=".csv,.pdf" className="hidden" onChange={handleUploadFile} multiple />
          <span className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1A362D] text-white text-sm font-medium rounded-md hover:bg-[#264B40] transition-colors">
            <Upload className="w-4 h-4" />
            Upload Files
          </span>
        </label>

        <Dialog open={showManual} onOpenChange={(open) => {
          if (open && requireAuth && !requireAuth()) return;
          setShowManual(open);
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-manual-btn" variant="outline" className="border-[#E7E5DF] hover:bg-[#F1EFEA]">
              <Plus className="w-4 h-4 mr-2" />
              Add Manual Entry
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-['Playfair_Display']">Add Transaction</DialogTitle>
              <DialogDescription>Enter transaction details manually.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label>Date</Label>
                <Input
                  data-testid="manual-date-input"
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  data-testid="manual-description-input"
                  placeholder="e.g. Client Payment - ABC Corp"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div>
                <Label>Amount (positive for deposits, negative for withdrawals)</Label>
                <Input
                  data-testid="manual-amount-input"
                  type="number"
                  step="0.01"
                  placeholder="e.g. 15000 or -3500"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                />
              </div>
              <Button data-testid="submit-manual-btn" onClick={handleAddManual} className="w-full bg-[#1A362D] hover:bg-[#264B40]">
                Add Transaction
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {transactions.length > 0 && (
          <Button
            data-testid="clear-all-btn"
            variant="ghost"
            onClick={handleClearAll}
            className="text-[#E07A5F] hover:text-[#E07A5F] hover:bg-[#E07A5F]/10 ml-auto"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear All
          </Button>
        )}
      </div>

      {/* Monthly Summary Cards */}
      {Object.keys(monthlySummary).length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          {Object.entries(monthlySummary)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([month, data]) => (
              <div key={month} className="bg-white border border-[#E7E5DF] rounded-md p-4">
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">{month}</p>
                <p className="text-sm font-medium text-[#2D4A3E] mt-1">
                  +${data.deposits.toLocaleString(undefined, { minimumFractionDigits: 0 })}
                </p>
                <p className="text-sm font-medium text-[#E07A5F]">
                  -${data.withdrawals.toLocaleString(undefined, { minimumFractionDigits: 0 })}
                </p>
                <p className="text-xs text-[#78716C] mt-1">{data.count} transactions</p>
              </div>
            ))}
        </div>
      )}

      {/* Transaction Table */}
      {transactions.length === 0 ? (
        <div className="bg-white border border-[#E7E5DF] rounded-md p-12 text-center">
          <FileSpreadsheet className="w-12 h-12 text-[#8A9A86] mx-auto mb-4" />
          <h3 className="text-xl font-['Playfair_Display'] font-semibold text-[#1C1917] mb-2">
            No Bank Statement Data
          </h3>
          <p className="text-[#57534E] text-sm max-w-md mx-auto">
            Upload CSV or PDF bank statements (multiple files supported), add transactions manually, or load sample data to begin your cash flow analysis.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-[#E7E5DF] rounded-md overflow-hidden">
          <div className="p-4 border-b border-[#E7E5DF] flex items-center justify-between">
            <h3 className="text-lg font-['Playfair_Display'] font-semibold text-[#1C1917]">
              Transactions ({transactions.length})
            </h3>
          </div>
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F1EFEA]">
                  <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">Date</TableHead>
                  <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">Description</TableHead>
                  <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] text-right">Amount</TableHead>
                  <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">Category</TableHead>
                  <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((tx) => (
                  <TableRow key={tx.id} className="table-row-hover">
                    <TableCell className="text-sm font-['IBM_Plex_Mono'] text-[#57534E] py-3">
                      {tx.date}
                    </TableCell>
                    <TableCell className="text-sm text-[#1C1917] py-3 max-w-[200px] truncate">
                      {tx.description}
                    </TableCell>
                    <TableCell className={`text-sm font-['IBM_Plex_Mono'] font-medium py-3 text-right ${
                      tx.amount > 0 ? "text-[#2D4A3E]" : "text-[#E07A5F]"
                    }`}>
                      {tx.amount > 0 ? "+" : ""}${Math.abs(tx.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="py-3">
                      <Select
                        value={tx.category}
                        onValueChange={(val) => handleCategoryChange(tx.id, val)}
                      >
                        <SelectTrigger
                          data-testid={`category-select-${tx.id}`}
                          className={`h-7 text-xs rounded-md px-2 border-0 w-auto min-w-[140px] ${categoryColors[tx.category] || "bg-[#F1EFEA]"}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map((cat) => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="py-3">
                      <button
                        data-testid={`delete-tx-${tx.id}`}
                        onClick={() => handleDelete(tx.id)}
                        className="text-[#78716C] hover:text-[#E07A5F] transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
